window.BOLD = window.BOLD || {};

window.BOLD.checkout = {
  getCookie: (cname) => {
    const name = `${cname}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i += 1) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  },
  setCookie: (cname, cvalue, expiry) => {
    let cookieString = `${cname}=${cvalue}`;
    if (expiry instanceof Date) {
      cookieString = `${cookieString}; expires=${expiry.toUTCString()}`;
    }
    document.cookie = `${cookieString}; path=/`;
  },
  addCartParam: (pluginId, name, value) => {
    let cartParams = window.BOLD.checkout.getCartParams(pluginId);
    cartParams = {
      ...cartParams,
      [name]: value,
    };
    window.BOLD.checkout.setCartParamsCookie(pluginId, cartParams);
  },
  getCartParams: (pluginId) => {
    const encodedCartParams = window.BOLD.checkout.getCookie('cartParams');
    if (!encodedCartParams) {
      return {};
    }
    const cartParams = window.BOLD.checkout.decodeCartParams(encodedCartParams);
    if (cartParams[pluginId] === undefined) {
      return {};
    }
    return cartParams[pluginId];
  },
  deleteCartParam: (pluginId, name) => {
    const cartParams = window.BOLD.checkout.getCartParams(pluginId);
    if (cartParams[name] !== undefined) {
      delete cartParams[name];
    }
    window.BOLD.checkout.setCartParamsCookie(pluginId, cartParams);
  },
  setCartParamsCookie: (pluginId, value) => {
    const cartParamsCookie = window.BOLD.checkout.getCookie('cartParams');
    let cartParams;
    if (!cartParamsCookie) {
      cartParams = {};
    } else {
      cartParams = window.BOLD.checkout.decodeCartParams(cartParamsCookie);
    }
    cartParams = {
      ...cartParams,
      [pluginId]: value,
    };
    window.BOLD.checkout.setCookie('cartParams', window.BOLD.checkout.encodeCartParams(cartParams));
  },
  encodeCartParams: cartParams => JSON.stringify(btoa(JSON.stringify(cartParams))),
  decodeCartParams: encodedCartParams => JSON.parse(atob((JSON.parse(encodedCartParams)))),
  localTime: () => {
    const localDate = new Date();
    return localDate.getTime();
  },
};

/* eslint-disable import/prefer-default-export */
export function totalPrice(lineItem) {
  const { centAmount: unitCentAmount, ...unitPrice } = lineItem.price.discounted?.value || lineItem.price.value;
  const originalPrice = {
    ...unitPrice,
    centAmount: unitCentAmount * lineItem.quantity,
  };
  const price = { value: { ...originalPrice } };
  if (originalPrice.centAmount !== lineItem.totalPrice.centAmount) {
    price.discounted = { value: { ...lineItem.totalPrice } };
  }
  return price;
}

// eslint-disable-next-line max-len
const createPricePoints = country => price => (price.country === country ? 1 : 0);
export const toPrice = (prices, {
  country, currency, customerGroup, channel,
}) => {
  const pricePonts = createPricePoints(country);
  return prices.filter(
    p => p.value.currencyCode === currency
      && p.customerGroup?.id === customerGroup
      && p.channel?.id === channel,
  )// sort mutates but filter copied prices so no problem
    .sort(
      (a, b) => pricePonts(b) - pricePonts(a),
    )[0];
};
export const pageFromRoute = (route) => {
  const pageNum = Number(route.params.page);
  const page = Number.isNaN(pageNum) || pageNum <= 1
    ? 1 : pageNum;
  return {
    page,
  };
};
export const pushPage = (page, component, name) => {
  const { params, query } = component.$route;
  component.$router.push({
    name,
    params: {
      ...params,
      page,
    },
    query,
  });
};
export const locale = component => component?.$route?.params?.locale;
export const isToughDevice = () => 'ontouchstart' in window;

export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()
      .split(';')
      .shift();
  }
  return '';
};

export const checkoutUrl = `https://${process.env.VUE_APP_BOLD_CHECKOUT_URL}`;

export const getBoldCheckoutUrl = (localeString, activeCartId, accessToken, returnToCartUrl) => {
  const platform = 'commercetools';
  const shop = window.location.host;
  const xsrf = getCookie('bold_checkout_cart_xsrf');
  const cartId = activeCartId;
  const boldCartParamsCurrencyIso = 'USD';
  const searchParams = new URLSearchParams();

  searchParams.set('platform', platform);
  searchParams.set('language_iso', localeString);
  searchParams.set('shop', shop);
  searchParams.set('bold_checkout_cart_xsrf', xsrf);
  searchParams.set('return_url', returnToCartUrl);
  searchParams.set('cart_id', cartId);
  searchParams.set('user_access_token', accessToken);
  searchParams.set('bold_cart_params[\'currency_iso_name\']', boldCartParamsCurrencyIso);

  const encodedCartParams = window.BOLD.checkout.getCookie('cartParams');
  if (encodedCartParams) {
    searchParams.set('cart_params', encodedCartParams);
  }

  const checkoutLocalTime = window.BOLD.checkout.localTime();

  let returnUrl = `${checkoutUrl}/boldplatform/checkout/build_order_begin?${searchParams.toString()}&customer_id=`;
  returnUrl = `${returnUrl}&checkout_local_time=${checkoutLocalTime}`;
  return returnUrl;
};

// get the user scoped access token for either logged in or anonymous
export const getUserAccessToken = async () => {
  const ctpSession = JSON.parse(window.localStorage.getItem('session'));
  return ctpSession.tokenInfo.access_token;
};

export const verifyBoldCart = async () => {
  const shopDomain = window.location.host;
  const platform = 'commercetools';

  if (!getCookie('bold_checkout_cart_xsrf')) {
    const response = await fetch(
      `${checkoutUrl}/boldplatform/cart-verify/token?shop=${shopDomain}&platform=${platform}`,
      { credentials: 'include' },
    );

    if (!response.ok) {
      throw new Error('Bold Checkout Token not set.');
    } else {
      const token = response.headers.get('bold_checkout_cart_xsrf');
      const date = new Date();
      const fiftenSeconds = 15000;
      const expiryDate = date.setTime(date.getTime() + fiftenSeconds);
      const uTCExpiry = new Date(expiryDate).toUTCString();
      document.cookie = `bold_checkout_cart_xsrf=${token}; expires=${uTCExpiry}; path=/;`;
    }
  }
};
